import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { api } from '@/lib/api';

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  organizationId: string | null;
  organizationName: string | null;
  role: string;
  mfaEnabled: boolean;
  mfaVerified: boolean;
  isSuperAdmin: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string) => Promise<{ success: boolean; error?: string }>;
  verify: (email: string, token: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const res = await api('/v1/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data.user || data);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (email: string) => {
    try {
      const res = await api('/v1/auth/magic-link', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        return { success: true };
      }
      const data = await res.json().catch(() => null);
      return { success: false, error: data?.error || 'Failed to send magic link' };
    } catch {
      return { success: false, error: 'Network error' };
    }
  }, []);

  const verify = useCallback(async (email: string, token: string) => {
    try {
      const res = await api('/v1/auth/verify', {
        method: 'POST',
        body: JSON.stringify({ email, token }),
      });
      if (res.ok) {
        await fetchUser();
        return { success: true };
      }
      const data = await res.json().catch(() => null);
      return { success: false, error: data?.error || 'Verification failed' };
    } catch {
      return { success: false, error: 'Network error' };
    }
  }, [fetchUser]);

  const logout = useCallback(async () => {
    try {
      await api('/v1/auth/logout', { method: 'POST' });
    } catch {
      // logout always succeeds client-side
    }
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await api('/v1/auth/refresh', { method: 'POST' });
      if (res.ok) {
        await fetchUser();
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
  }, [fetchUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        verify,
        logout,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
